import {useContext, useEffect} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {AxiosError} from 'axios';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import Badge from 'patient-ping-remedy/packages/badge';
import ContextMenu from 'patient-ping-remedy/packages/context-menu';
import DatePicker from 'patient-ping-remedy/packages/date-picker';
import {StandardSemiBoldTypography, StandardTypography} from 'patient-ping-remedy/packages/typography';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {
  CareJourneyDetailsResponse,
  CareJourneyPriority,
  CareJourneyRequest,
  InitiationReason,
  JourneyInitiationMode,
  TaskStepName,
} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {createFormFieldConfig, useForm} from '@/hooks/useForm';
import {
  StepLink,
  Content,
  CustomDatePickerWrapper,
  Section,
  Title
} from '@/components/pages/tasks/task_edit/progress/TaskEditDetails.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import AssignedToTask from "@/components/pages/tasks/AssignedToTask";
import AiWorkingBanner from "@common/AiWorkingBanner";
import {MixpanelEventType, MixpanelTaskEvent} from "@/api/dto/mixpanel";
import {useTaskStepName} from "@/hooks/useTaskStepName";
import {useMixpanel} from "@/hooks/useMixpanel";
import {isEqual} from "lodash-es";
import {FilterInvalidate} from "@common/FilterComponent";
import CareNoteSection from "@/components/pages/tasks/task_edit/progress/CareNoteSection";
import CareTeamReferralSection from "@/components/pages/tasks/task_edit/progress/CareTeamReferralSection";
import {
  appointmentTypes,
  TERMINAL_APPOINTMENT_STATUSES
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants";
import {SearchParam} from "@/helpers/search_params";
import {useNavigate} from "react-router-dom";
import EscalationSection from "@/components/pages/tasks/task_edit/progress/EscalationSection";

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
  changeStep?(step: number): void;
  disabled: boolean;
  invalidate: FilterInvalidate;
}

export default function TaskEditDetails({ careJourneyDetails, changeStep, disabled, invalidate }: Props) {
  const queryClient = useQueryClient();
  const { carecoApi } = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const { addAlert } = useAlertStore();
  const { taskStepDisplayValues } = useTaskStepName();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const navigate = useNavigate();

  const { mutate: save, isPending } = useMutation({
    mutationFn: async (careJourneyRequest: CareJourneyRequest) =>
      carecoApi?.updateCareJourney(careJourneyDetails.careJourneyInstanceId, currentRoster!.id, careJourneyRequest),
    onSuccess() {
      if(mixpanelEventProperties) {
        if (!isEqual(new Date(careJourneyDetails.dueDate), new Date(form.dueDate.value))) {
          const event: MixpanelTaskEvent = {
            eventName: MixpanelEventType.BIH_TASK_DUE_DATE_CHANGED,
            eventType: careJourneyDetails.eventType,
            careJourneyName: careJourneyDetails.journeyType,
            ...mixpanelEventProperties!,
            ...getCurrentGroupDetails(currentRoster!)
          };
          carecoApi?.postMixpanelEvent(event);
        }
        if (careJourneyDetails.priority != form.priority.value) {
          const event: MixpanelTaskEvent = {
            eventName: MixpanelEventType.BIH_PRIORITY_CHANGED_IN_TASK,
            eventType: careJourneyDetails.eventType,
            careJourneyName: careJourneyDetails.journeyType,
            ...mixpanelEventProperties!,
            ...getCurrentGroupDetails(currentRoster!)
          };
          carecoApi?.postMixpanelEvent(event);
        }
      }
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Care journey updated successfully.',
      });
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['taskSummary', currentRoster?.id] });
      setSubmitted(true);
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to update care journey. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const { form, isValid, isFormChanged, setInitialForm, handleChange, handleSubmit, submitted, setSubmitted } = useForm(() => {
    let dueDate = null;
    if(form.dueDate.value) {
      dueDate = new Date(form.dueDate.value);
      dueDate.setHours(23, 59, 59, 999);
      dueDate = dueDate.toISOString();
    }

    save({
      priority: form.priority.value,
      dueDate: dueDate,
    });
  });

  useEffect(() => {
    setInitialForm({
      ...createFormFieldConfig(
        'priority',
        careJourneyDetails.priority ?? null,
        { valid: true, message: '' },
        [],
        false,
      ),
      ...createFormFieldConfig(
        'dueDate',
        careJourneyDetails.dueDate ? new Date(careJourneyDetails.dueDate) : null,
        { valid: true, message: '' },
        [],
        false,
      ),
    });
  }, [careJourneyDetails]);

  const priorityMenuItems = !form.priority?.value ?
    disabled ? [] :
      [{
        label: 'Urgent',
        action: () => {
          handleChange(null, 'priority', CareJourneyPriority.URGENT);
        },
      }] : [{
      label: 'None',
      action: () => {
        handleChange(null, 'priority', null);
      },
    }];

  const currentStepIndex = careJourneyDetails.steps.findIndex((step) =>
    step.stepName === careJourneyDetails.currentStep);

  const { orchestrationEngineDetails } = careJourneyDetails;

  const displayableInitiationReasons = orchestrationEngineDetails?.initiationReasons
    ?.filter(reason =>
      reason === InitiationReason.HIGH_RISK
      || reason === InitiationReason.NEEDS_EXCLUSION_REVIEW
    ) ?? [];
  const initiationReasonsBannerText = displayableInitiationReasons.length > 0
    ? orchestrationEngineDetails?.initiationMode === JourneyInitiationMode.AI
      ? `Warning: ${displayableInitiationReasons.join(', ')}`
      : `Assigned to HITL for ${displayableInitiationReasons.join(', ')}`
    : null;

  return (
    <div>
      <Title>Details</Title>
      <StandardTypography>
        AI-Assisted Transitional Care Management to facilitate patient engagement, intake including medication review, and
        appointment scheduling all while ensuring compliance with CMS requirements
      </StandardTypography>
      {orchestrationEngineDetails && orchestrationEngineDetails.actor !== 'human' &&
        <AiWorkingBanner />}
      {orchestrationEngineDetails && orchestrationEngineDetails.unassignedByAi
        && orchestrationEngineDetails.reasonStep === careJourneyDetails.currentStep
        && orchestrationEngineDetails.actor === 'human' && (
        <Banner type={BannerTypes.WARNING} className={css({ marginTop: sizes.medium })}>
          AI agent couldn’t complete{' '}
          <StepLink onClick={() => changeStep?.(currentStepIndex)}>
            {taskStepDisplayValues[
              orchestrationEngineDetails.reasonStep as TaskStepName
            ] ?? orchestrationEngineDetails.reasonStep}
          </StepLink>.{' '}
          {orchestrationEngineDetails.reason}.
        </Banner>
      )}
      {initiationReasonsBannerText && (
        <Banner type={BannerTypes.WARNING} className={css({ marginTop: sizes.medium })}>
          {initiationReasonsBannerText}
        </Banner>
      )}

      {careJourneyDetails.steps.filter(step => step.stepName === TaskStepName.SCHEDULED_APPOINTMENTS)
        .flatMap(step => step.appointments)
        .filter(appointment => appointment
            && !appointment.userAssignment.assignedToAi
            && appointment.orchestrationEngineDetails?.unassignedByAi
            && !TERMINAL_APPOINTMENT_STATUSES.has(appointment.status?.toLowerCase())
        )
        .map((appointment, _) => {
          const appointmentType = appointmentTypes[appointment!.providerType];
          const reasonStep = appointment?.orchestrationEngineDetails?.reasonStep;
          const reminderStep = reasonStep !== TaskStepName.SCHEDULED_APPOINTMENT
            && reasonStep !== TaskStepName.APPOINTMENT_CONFIRMATION;
          const searchParams = `${SearchParam.TASK_ID}=${careJourneyDetails.careJourneyInstanceId}&` +
            `${SearchParam.ROSTER_ID}=${currentRoster?.id}&` +
            `${SearchParam.APPOINTMENT_ID}=${appointment!.careJourneyInstanceId}`;

          return <div key={appointment!.careJourneyInstanceId}>
            <Banner type={BannerTypes.WARNING} className={css({marginTop: sizes.medium})}>
              For { appointmentType ?? 'an appointment'}, AI agent couldn't
              {reminderStep ? ' send an SMS reminder' : ` complete ${taskStepDisplayValues[reasonStep]}`}.
              Navigate to {' '}
              <StepLink onClick={() => navigate(`/tasks/details/appointment?${searchParams}`)}>
                {appointmentType ?? 'appointment'}
              </StepLink> to manually
              {reminderStep ? ' send a reminder for the ' :
                reasonStep === TaskStepName.SCHEDULED_APPOINTMENT ? ' schedule an ' : ' confirm the '}
              appointment.
            </Banner>
          </div>;
        })
      }

      {!Helpers.isEmptyObject(form) && (
        <Content>
          <Section>
            <StandardSemiBoldTypography>Assigned to task</StandardSemiBoldTypography>
            <div>
              <AssignedToTask
                careJourney={careJourneyDetails}
                showAiAssignment={!!orchestrationEngineDetails}
              />
            </div>
          </Section>
          <Section>
            <StandardSemiBoldTypography>Due date</StandardSemiBoldTypography>
            <CustomDatePickerWrapper>
              <DatePicker
                disabled={disabled}
                minDate={new Date()}
                value={form.dueDate.value}
                onChange={(date) => {
                  handleChange(null, 'dueDate', date);
                  return date;
                }}
              />
            </CustomDatePickerWrapper>
          </Section>
          <Section>
            <StandardSemiBoldTypography>Priority</StandardSemiBoldTypography>
            <ContextMenu menuItems={priorityMenuItems}>
              <Badge
                backgroundColor={
                  disabled ? colors.gray1 :
                    form.priority.value === CareJourneyPriority.URGENT ? colors.red1 : colors.gray1}
                textColor={
                  disabled ? colors.gray4 :
                    form.priority.value === CareJourneyPriority.URGENT ? colors.red5 : colors.charcoal}
              >
                {form.priority.value ? DisplayHelpers.humanize(form.priority.value) : 'None'}
                {!disabled && 
                  <Icon
                    iconClass={ICONS['angle-down']}
                    iconSize="lg"
                    weight="far"
                    className={css({ marginLeft: sizes.xxSmall })}
                  />}
              </Badge>
            </ContextMenu>
          </Section>
          <Section>
            <StandardSemiBoldTypography>Task type</StandardSemiBoldTypography>
            <Badge backgroundColor={colors.gray1} textColor={colors.charcoal}>
              {careJourneyDetails.journeyType}
            </Badge>
          </Section>
          {currentRoster && (
            <Section>
              <StandardSemiBoldTypography>Group</StandardSemiBoldTypography>
              <Badge backgroundColor={colors.gray1} textColor={colors.charcoal}>
                {currentRoster.name}
              </Badge>
            </Section>
          )}
          <EscalationSection careJourneyDetails={careJourneyDetails} />
          <CareNoteSection careJourneyDetails={careJourneyDetails} />
          <CareTeamReferralSection careJourneyDetails={careJourneyDetails} />
          <Section>
            <div />
            <Button disabled={disabled || !isValid() || !isFormChanged() || isPending || submitted} onClick={handleSubmit}>
              Save
            </Button>
          </Section>
        </Content>
      )}
    </div>
  );
}
